import { type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useBasePath } from '../../../lib/config/nav-helpers';
import { MetricCard, AnimatedCounter } from '../../../components/ui';
import { KpiPicker } from '../../../components/forms/KpiPicker';
import { kpiHint } from '../../../lib/glossary';
import { INTEGRATIONS_PATH, NOT_CONNECTED, kpiGap } from '../../../lib/coverage';
import { getClientHealth } from '../../../lib/client-health';
import { getSalesToday, SALES_TODAY_KEY } from '../../../lib/sales-today';
import type { KpiDefinition } from '../../../lib/config/kpi-config';
import type { RevenueForecast } from '../../../lib/revenue-forecast';

// ─── Props ───────────────────────────────────────────────────────────

export interface KpiRibbonProps {
  selectedKpis: KpiDefinition[];
  selectedIds: string[];
  setSelection: (ids: string[]) => void;
  showKpiPicker: boolean;
  setShowKpiPicker: (v: boolean) => void;
  revenue: { data?: RevenueForecast };
  dealRisk: { data?: any };
  followUps: { data?: any };
  escalations: { data?: any };
}

// ─── Component ───────────────────────────────────────────────────────

// Each KPI's own period. The revenue forecast is quarter-to-date; the counts are
// what is open right now; win rate is the deal book's 90-day figure. Nothing here
// has a prior-period value yet, so no card shows a trend arrow.
const QTD = 'quarter to date';
const NOW = 'open right now';

export function KpiRibbon({
  selectedKpis,
  selectedIds,
  setSelection,
  showKpiPicker,
  setShowKpiPicker,
  revenue,
  dealRisk,
  followUps,
  escalations,
}: KpiRibbonProps) {
  const navigate = useNavigate();
  const basePath = useBasePath();

  // Fetched only when the owner has picked the KPI: /v1/sales/today builds the Deal Coach.
  const clientHealth = useQuery({ queryKey: ['client-health'], queryFn: getClientHealth, staleTime: 5 * 60_000, enabled: selectedIds.includes('active_clients') });
  const salesToday = useQuery({ queryKey: SALES_TODAY_KEY, queryFn: getSalesToday, staleTime: 5 * 60_000, enabled: selectedIds.includes('win_rate') || selectedIds.includes('evidence_forecast') });

  type KpiResult = { value: ReactNode; explain?: { metric: string; current: number; prior?: number; unit?: string; period: string } };

  function resolveKpiValue(kpi: KpiDefinition): KpiResult {
    const f = revenue.data;
    const dr = dealRisk.data;
    const fu = followUps.data;
    const esc = escalations.data;
    switch (kpi.id) {
      case 'revenue_collected': return { value: f?.revenueCollected != null ? <AnimatedCounter value={f.revenueCollected} format="currency" decimals={2} /> : '—', explain: f?.revenueCollected != null ? { metric: 'Revenue Collected', current: f.revenueCollected, unit: '$', period: QTD } : undefined };
      case 'pipeline_value': return { value: f?.pipelineRaw != null ? <AnimatedCounter value={f.pipelineRaw} format="currency" decimals={1} /> : '—', explain: f?.pipelineRaw != null ? { metric: 'Pipeline Value', current: f.pipelineRaw, unit: '$', period: NOW } : undefined };
      case 'deals_at_risk': return { value: dr?.summary ? <AnimatedCounter value={dr.summary.atRisk} format="number" /> : '—', explain: dr?.summary ? { metric: 'Deals at Risk', current: dr.summary.atRisk, unit: 'deals', period: NOW } : undefined };
      case 'followups_due': return { value: fu ? <AnimatedCounter value={fu.items.length} format="number" /> : '—', explain: fu ? { metric: 'Follow-ups Due', current: fu.items.length, unit: 'items', period: NOW } : undefined };
      case 'projected_revenue': return { value: f?.projectedRevenue != null ? <AnimatedCounter value={f.projectedRevenue} format="currency" decimals={1} /> : '—', explain: f?.projectedRevenue != null ? { metric: 'Projected Revenue', current: f.projectedRevenue, unit: '$', period: 'this quarter, at the QTD pace' } : undefined };
      case 'revenue_goal': return { value: f?.revenueGoal ? <AnimatedCounter value={f.revenueGoal} format="currency" decimals={1} /> : '—' };
      case 'weekly_close_rate': return { value: f?.weeklyCloseRateNeeded != null ? `${(f.weeklyCloseRateNeeded * 100).toFixed(1)}%` : '—', explain: f?.weeklyCloseRateNeeded != null ? { metric: 'Weekly Close Rate Needed', current: f.weeklyCloseRateNeeded * 100, unit: '%', period: 'rest of this quarter' } : undefined };
      case 'active_clients': {
        const n = clientHealth.data?.summary.totalClients;
        return { value: n != null ? <AnimatedCounter value={n} format="number" /> : '—', explain: n != null ? { metric: 'Active Clients', current: n, unit: 'clients', period: 'on the client list now' } : undefined };
      }
      case 'escalations': return { value: esc ? <AnimatedCounter value={esc.actions.length} format="number" /> : '—', explain: esc ? { metric: 'Escalations', current: esc.actions.length, unit: 'items', period: NOW } : undefined };
      case 'win_rate': {
        const wr = salesToday.data?.pipeline.winRate90d;
        // null rate = nothing closed in 90 days; no number is the honest answer.
        return { value: wr?.rate != null ? `${(wr.rate * 100).toFixed(1)}%` : '—', explain: wr?.rate != null ? { metric: 'Win Rate', current: wr.rate * 100, unit: '%', period: `last 90 days (${wr.won} won, ${wr.lost} lost)` } : undefined };
      }
      case 'evidence_forecast': {
        // The same book as Sales Today, so the CRM figure here equals its open pipeline.
        const ev = salesToday.data?.crmConnected ? salesToday.data.evidence : null;
        return { value: ev ? <AnimatedCounter value={ev.evidenceCents / 100} format="currency" decimals={1} /> : '—', explain: ev ? { metric: 'Evidence Forecast', current: ev.evidenceCents / 100, unit: '$', period: `${NOW}, of $${Math.round(ev.crmCents / 100).toLocaleString('en-US')} the CRM shows` } : undefined };
      }
      // Total Deal Value is the pipeline total, reported as-is.
      case 'total_deal_value': return { value: f?.pipelineRaw != null ? <AnimatedCounter value={f.pipelineRaw} format="currency" decimals={1} /> : '—', explain: f?.pipelineRaw != null ? { metric: 'Total Deal Value', current: f.pipelineRaw, unit: '$', period: NOW } : undefined };
      default: return { value: '—' };
    }
  }

  return (
    <>
      <div className="group/kpi relative">
        <div className="flex items-center justify-between mb-2">
          <span className="text-2xs font-semibold uppercase tracking-wider text-ink-dim">Key Metrics</span>
          <button
            onClick={() => setShowKpiPicker(true)}
            className="flex items-center gap-1 rounded-lg px-2 py-1 text-2xs text-ink-dim opacity-0 transition-all hover:bg-surface-2 hover:text-ink group-hover/kpi:opacity-100 focus-visible:opacity-100"
            title="Customize KPIs"
          >
            <svg aria-hidden="true" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" /></svg>
            Customize
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 animate-stagger-in">
          {selectedKpis.map(kpi => {
            const { value, explain } = resolveKpiValue(kpi);
            // The coverage rule: a source Waypoint cannot see gets an invitation, not a "0".
            // Follow-ups and Escalations list real items, so only an EMPTY list is a claim ("0"): it needs its own sources.
            const gap = kpi.id === 'followups_due' ? ((followUps.data?.items.length ?? 0) > 0 ? null : kpiGap(kpi.id, followUps.data?.coverage))
              : kpi.id === 'escalations' ? ((escalations.data?.actions.length ?? 0) > 0 ? null : kpiGap(kpi.id, escalations.data?.coverage))
                : kpiGap(kpi.id, revenue.data?.coverage ?? dealRisk.data?.coverage);
            return (
              <MetricCard
                key={kpi.id}
                label={kpi.label}
                value={gap ? <span className="text-xs font-medium text-ink-dim">{NOT_CONNECTED[gap].action}</span> : value}
                accent={kpi.accent}
                sparkData={kpi.sparkData}
                hint={gap ? NOT_CONNECTED[gap].cant : kpiHint(kpi.id)}
                onClick={() => navigate(gap ? `${basePath}${INTEGRATIONS_PATH}` : `${basePath}${kpi.navTarget}`)}
                explainData={explain}
              />
            );
          })}
        </div>
      </div>
      {showKpiPicker && <KpiPicker selectedIds={selectedIds} onSave={setSelection} onClose={() => setShowKpiPicker(false)} />}
    </>
  );
}
